import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  Trophy,
  User,
  Wallet,
  X,
} from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";

// Trimmed to 5 essentials for the desktop bar — keeps the layout fitting at 1280px+.
// The full list lives below for the mobile/hamburger menu so nothing is lost.
const desktopNavLinks = [
  { href: "/games", label: "Events" },
  { href: "/creators", label: "Creators" },
  { href: "/leaderboard", label: "Leaderboard" },
  { href: "/pricing", label: "Pricing" },
  { href: "/faq", label: "FAQ" },
];

const mobileNavLinks = [
  { href: "/games", label: "Events" },
  { href: "/players", label: "Players" },
  { href: "/creators", label: "Creators" },
  { href: "/promoters", label: "Promoters" },
  { href: "/leaderboard", label: "Leaderboard" },
  { href: "/#features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/#how-it-works", label: "How It Works" },
  { href: "/faq", label: "FAQ" },
];

export default function PublicNav() {
  const { isAuthenticated, user, logout } = useAuth();
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Beta banner: shown while the BETA coupon is active (deactivate the coupon to end the beta).
  const betaStatus = trpc.coupon.betaStatus.useQuery(undefined, { staleTime: 5 * 60_000 });
  const betaActive = betaStatus.data?.active === true;
  useEffect(() => {
    if (!betaActive) return;
    document.body.classList.add("gp-beta-banner");
    return () => document.body.classList.remove("gp-beta-banner");
  }, [betaActive]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = href;
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.replace("/");
  };

  const initials = user?.name?.charAt(0)?.toUpperCase() || "U";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/40">
      {betaActive && (
        <div className="h-8 flex items-center justify-center gap-2 px-3 bg-neon/15 border-b border-neon/30 text-[12px] sm:text-[13px] leading-none text-center">
          <span className="font-extrabold tracking-widest text-neon">BETA</span>
          <span className="text-slate-200 hidden md:inline">
            Creating an event is free: the $20 event fee is waived with code <b className="text-white">BETA</b>.
          </span>
          <span className="text-slate-200 md:hidden">
            Free to create events · code <b className="text-white">BETA</b>
          </span>
          <Link href="/create">
            <span className="hidden sm:inline text-neon font-semibold whitespace-nowrap cursor-pointer hover:underline underline-offset-2">
              Start an event →
            </span>
          </Link>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/">
          <div className="flex items-center gap-2.5 cursor-pointer">
            <div className="w-9 h-9 rounded-lg bg-neon/15 border border-neon/30 flex items-center justify-center">
              <span className="text-neon font-extrabold text-sm tracking-tighter">GP</span>
            </div>
            <span className="text-xl font-extrabold tracking-tight uppercase">
              <span className="text-white">Game</span>
              <span className="text-neon">Point</span>
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden xl:flex items-center gap-1">
          {desktopNavLinks.map((link) => (
            <Link key={link.href} href={link.href.startsWith("/#") ? "/" : link.href}>
              <Button
                variant="ghost"
                onClick={() => handleNavClick(link.href)}
                className={`text-sm font-semibold uppercase tracking-wide ${
                  location === link.href || (link.href !== "/" && location.startsWith(link.href.split("#")[0]) && !link.href.startsWith("/#"))
                    ? "text-neon"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {link.label}
              </Button>
            </Link>
          ))}
        </div>

        {/* Desktop right side */}
        <div className="hidden xl:flex items-center gap-2">
          <LanguageSelector variant="compact" />

          {isAuthenticated ? (
            <>
              {/* Console button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/console">
                    <Button
                      variant="outline"
                      className="border-neon/30 text-neon hover:bg-neon/10 font-bold uppercase tracking-wide gap-2"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Console
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  Main dashboard for players, creators, promoters, and admins.
                </TooltipContent>
              </Tooltip>

              {/* Profile dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors focus:outline-none">
                    <div className="w-8 h-8 rounded-full bg-neon/20 border border-neon/30 flex items-center justify-center text-neon font-bold text-xs">
                      {initials}
                    </div>
                    <span className="max-w-[120px] truncate">{user?.name || "User"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link href="/console/profile" className="flex items-center gap-2 cursor-pointer">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/console/tournaments" className="flex items-center gap-2 cursor-pointer">
                      <Trophy className="w-4 h-4" />
                      My Games
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/console/wallet" className="flex items-center gap-2 cursor-pointer">
                      <Wallet className="w-4 h-4" />
                      Wallet / Earnings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/console/settings" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              {/* Sign In link */}
              <Link href="/login">
                <Button
                  variant="ghost"
                  className="border border-border/50 text-slate-300 hover:text-white hover:border-border font-bold uppercase tracking-wide"
                >
                  Sign In
                </Button>
              </Link>
              {/* Start an event CTA */}
              <Link href="/create">
                <Button className="bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide px-4">
                  Start an Event
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="xl:hidden text-slate-300 hover:text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-background/95 backdrop-blur-xl border-t border-border/40 px-6 py-4 space-y-1">
          {mobileNavLinks.map((link) => (
            <Link key={link.href} href={link.href.startsWith("/#") ? "/" : link.href}>
              <button
                onClick={() => handleNavClick(link.href)}
                className={`block w-full text-left py-2.5 text-sm font-bold uppercase tracking-wide ${
                  location === link.href ? "text-neon" : "text-slate-400"
                }`}
              >
                {link.label}
              </button>
            </Link>
          ))}

          <div className="py-2">
            <LanguageSelector />
          </div>

          <div className="pt-3 border-t border-border/30 flex flex-col gap-2">
            {isAuthenticated ? (
              <>
                {/* Authenticated mobile menu */}
                <div className="flex items-center gap-2 py-2 text-sm text-slate-300">
                  <div className="w-8 h-8 rounded-full bg-neon/20 border border-neon/30 flex items-center justify-center text-neon font-bold text-xs">
                    {initials}
                  </div>
                  <span className="font-semibold truncate">{user?.name || "User"}</span>
                </div>
                <Link href="/console">
                  <Button
                    variant="outline"
                    className="w-full border-neon/30 text-neon font-bold uppercase gap-2"
                    onClick={() => setMobileOpen(false)}
                    title="Main dashboard for players, creators, promoters, and admins."
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Console
                  </Button>
                </Link>
                <Link href="/console/profile">
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 w-full py-2 text-sm text-slate-400 hover:text-white"
                  >
                    <User className="w-4 h-4" /> Profile
                  </button>
                </Link>
                <Link href="/console/tournaments">
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 w-full py-2 text-sm text-slate-400 hover:text-white"
                  >
                    <Trophy className="w-4 h-4" /> My Games
                  </button>
                </Link>
                <Link href="/console/wallet">
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 w-full py-2 text-sm text-slate-400 hover:text-white"
                  >
                    <Wallet className="w-4 h-4" /> Wallet / Earnings
                  </button>
                </Link>
                <Link href="/console/settings">
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 w-full py-2 text-sm text-slate-400 hover:text-white"
                  >
                    <Settings className="w-4 h-4" /> Settings
                  </button>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full py-2 text-sm text-destructive hover:text-destructive/80"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                {/* Unauthenticated mobile menu */}
                <Link href="/login">
                  <Button
                    variant="outline"
                    className="w-full border-border text-slate-300 font-bold uppercase"
                    onClick={() => setMobileOpen(false)}
                  >
                    Sign In
                  </Button>
                </Link>
                <Link href="/create">
                  <Button
                    className="w-full bg-neon hover:bg-neon-dim text-background font-extrabold uppercase gap-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    Start an Event
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
